import React from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { UnitToggle } from '../common/UnitToggle';
import { WeightInput } from '../common/WeightInput';
import type { InventoryItem } from '../../types/inventory';
import type { FinishedGoodsProductionFormValues, FinalProductMode } from '../../hooks/inventory-hooks/useFinishedGoodsProductionForm';
import type { WeightUnit } from '../../hooks/useWeightUnit';
import { AutocompleteSelect, type AutocompleteOption } from '../common/AutocompleteSelect';
import { useLanguage } from '../../features/language/useLanguage';

interface FinishedGoodsFinalProductSectionProps {
  control: Control<FinishedGoodsProductionFormValues>;
  register: UseFormRegister<FinishedGoodsProductionFormValues>;
  errors: FieldErrors<FinishedGoodsProductionFormValues>;
  isSubmitted: boolean;
  showValidation?: boolean;
  isFinalProductMissing?: boolean;
  isFinalProductDuplicate?: boolean;
  isPackSizeMissing?: boolean;
  unit: WeightUnit;
  toggleUnit: () => void;
  finalProductMode: FinalProductMode;
  readOnly?: boolean;
  disableFinalProductSelect?: boolean;
  showPackSize?: boolean;
  showLocation?: boolean;
  finishedGoodsOptions: (AutocompleteOption & InventoryItem)[];
  onSwitchToNew?: (name: string) => void;
}

export const FinishedGoodsFinalProductSection: React.FC<FinishedGoodsFinalProductSectionProps> = ({
  control,
  register,
  errors,
  isSubmitted,
  showValidation = false,
  isFinalProductMissing = false,
  isFinalProductDuplicate = false,
  isPackSizeMissing = false,
  unit,
  toggleUnit,
  finalProductMode,
  readOnly = false,
  disableFinalProductSelect = false,
  showPackSize = true,
  showLocation = true,
  finishedGoodsOptions,
  onSwitchToNew,
}) => {
  const { t } = useLanguage();
  const shouldShowValidation = isSubmitted || showValidation;
  const finalProductError =
    errors.finalProductItemId?.message ||
    errors.finalProductName?.message ||
    (shouldShowValidation && isFinalProductMissing ? t('inventory.finishedGoods.finalProduct.required') : undefined) ||
    (finalProductMode === 'new' && isFinalProductDuplicate
      ? t('inventory.finishedGoods.finalProduct.nameAlreadyExists')
      : undefined);
  const packSizeError = showPackSize && shouldShowValidation && isPackSizeMissing ? t('inventory.finishedGoods.finalProduct.packSizeRequired') : undefined;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
  {/* Kolom Kiri: Final Product & Pack Size */}
  <div className="space-y-3">
    {/* Header Label */}
    <div className="flex items-center justify-between h-6"> 
      <label className="block text-sm font-semibold text-gray-800 tracking-tight">{t('inventory.finishedGoods.finalProduct.title')}</label>
      {showPackSize && (
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t('inventory.finishedGoods.finalProduct.packSize')}</span>
          <UnitToggle unit={unit} onToggle={toggleUnit} disabled={readOnly} />
        </div>
      )}
    </div>

    <div className="flex items-start gap-3">
      <div className="flex-1">
        {finalProductMode === 'existing' ? (
          <Controller
            control={control}
            name="finalProductItemId"
            render={({ field }) => (
              <AutocompleteSelect
                value={field.value || ''}
                onChange={(text, option) => {
                  if (!option && text.trim().length > 0) {
                    onSwitchToNew?.(text.trim());
                    return;
                  }
                  field.onChange(option?.id || '');
                }}
                options={finishedGoodsOptions}
                disabled={readOnly || disableFinalProductSelect}
                placeholder={t('inventory.finishedGoods.finalProduct.selectFinishedGoods')}
                error={finalProductMode === 'existing' ? finalProductError : undefined}
              />
            )}
          />
        ) : (
          <Controller
            control={control}
            name="finalProductName"
            render={({ field }) => (
              <input
                {...field}
                type="text"
                data-test-id="finished-goods-final-name"
                disabled={readOnly}
                className={`p-2.5 block w-full rounded-lg shadow-sm sm:text-sm disabled:bg-gray-100 transition-all duration-200 ${
                  (shouldShowValidation && isFinalProductMissing) || isFinalProductDuplicate
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
                placeholder={t('inventory.finishedGoods.finalProduct.enterNewName')}
              />
            )}
          />
        )}
      </div>

      {showPackSize && (
        <div className="w-28">
          <Controller
            control={control}
            name="packSizeKg"
            render={({ field }) => (
              <WeightInput
                value={field.value}
                onChange={field.onChange}
                unit={unit}
                disabled={readOnly}
                data-test-id="finished-goods-final-packsize"
                className={`w-full p-2.5 rounded-lg border shadow-sm text-sm font-medium text-right disabled:bg-gray-100 transition-all duration-200 ${
                  shouldShowValidation && isPackSizeMissing
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
                placeholder={t('inventory.finishedGoods.finalProduct.packSizePlaceholder')}
              />
            )}
          />
        </div>
      )}
    </div>
    
    {/* Error Messages Container */}
    <div className="min-h-[20px]">
      {packSizeError && <p className="text-[11px] font-medium text-red-600 ml-1">{packSizeError}</p>}
      {finalProductMode === 'new' && finalProductError && (
        <p className="text-[11px] font-medium text-red-600 ml-1">{finalProductError}</p>
      )}
    </div>
  </div>

  {/* Kolom Kanan: Location */}
  {showLocation && (
    <div className="space-y-3">
      <div className="h-6 flex items-center">
        <label className="block text-sm font-semibold text-gray-800 tracking-tight">{t('inventory.modal.fields.location')}</label>
      </div>
      <input
        type="text"
        disabled={readOnly}
        {...register('finalProductLocation')}
        className="p-2.5 block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100 transition-all duration-200"
        placeholder={t('inventory.modal.fields.locationPlaceholder')}
      />
      {/* Spacer agar sejajar dengan kolom kiri yang punya error message */}
      <div className="min-h-[20px]" />
    </div>
  )}
</div>
  );
};
